import React, { Component, ErrorInfo, ReactNode } from 'react';
import styles from '@/styles/components/models-gallery/ModelsGalleryErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ModelsGalleryErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ModelsGallery error:', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private renderErrorContent() {
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.icon}>⚠️</div>
          <h2 className={styles.title}>Unable to load models</h2>
          <p className={styles.message}>
            There was a problem loading the models gallery. This might be due to a network issue or temporary service problem.
          </p>
          <div className={styles.buttonContainer}>
            <button 
              onClick={this.handleRetry} 
              className={`${styles.button} ${styles.primaryButton}`}
            >
              Try Again
            </button>
            <button 
              onClick={this.handleReload} 
              className={`${styles.button} ${styles.secondaryButton}`}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  public override render() {
    if (this.state.hasError) {
      return this.renderErrorContent();
    }

    return this.props.children;
  }
}

export default ModelsGalleryErrorBoundary;